import React, { useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Radio,
  RadioGroup,
  Chip,
  IconButton,
  Divider,
  Tabs,
  Tab,
  Grid,
  Paper,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Autocomplete,
  Checkbox,
  OutlinedInput
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  DragIndicator as DragIndicatorIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  Inventory2Outlined as ArchiveIcon
} from '@mui/icons-material';
import { ThemeContext } from '../ThemeContext/ThemeContext';
import { connect } from 'react-redux';
import {
  adicionarColunaNoInicio,
  buildArchiveColumn,
  buildInitialKanbanColumnsFromStatuses,
  getUnassignedKanbanItems,
  isArchiveColumn,
  kanbanItemKey,
  parseKanbanColumnsFromFilter,
} from '../../utils/kanbanFilterItems';
import { addFilter, updateFilter, deleteFilter, fetchFilters } from '../../redux/filter/filter.actions';
import { getUsers } from '../../redux/user/user.actions';
import http from '../../services/http';
import { toast } from 'react-toastify';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { ptBR } from 'date-fns/locale';
import { DATE_PERIOD_OPERATORS, DATE_PERIOD_LABELS, isValuelessDatePeriodOperator } from '../../utils/datePeriods';
import { ROTULOS, aceitaAgenteLogado, ehValorDinamico, opcoesDeOperador, rotuloDoOperador, valoresDinamicosDe } from '../../utils/filtroDinamico';
import { ROTULO_EQUIPE, ehCompartilhado, mostrarOInterruptorAntigo, rotulosDeCompartilhamento } from '../../utils/compartilhamentoDoFiltro';
import { ehOperadorDeLista, valoresDaCondicao } from '../../utils/condicaoDeLista';
import { avisoDeCondicaoQueNuncaCasa } from '../../utils/condicaoQueNuncaCasa';
import { avisoDeCondicaoNaoAdicionada } from '../../utils/condicaoNaoAdicionada';
import {
  SELECAO_VAZIA,
  alternarSelecao,
  estaSelecionado,
  itensParaArrastar,
  quantidadeSelecionada,
} from '../../utils/selecaoDeJustificativas';

const PERMISSION_CREATE_DEFAULT_FILTERS = '#criar-filtros-padrao';

const DATETIME_FILTER_OPERATORS = [
  // Períodos relativos (item 52/9): mês atual, semana, hoje, intervalo etc.
  ...DATE_PERIOD_OPERATORS,
  'equals',
  'not_equals',
  'greater_than',
  'less_than',
  'greater_than_or_equal',
  'less_than_or_equal',
  'contains',
  'not_contains',
  'is_null',
  'is_not_null',
];

const TEXT_FILTER_OPERATORS = [
  'equals',
  'not_equals',
  'contains',
  'not_contains',
  'starts_with',
  'ends_with',
  'is_null',
  'is_not_null',
];

/*
 * "É um de" / "Não é um de" — só para campo com lista de opções (status,
 * serviço, departamento, categoria...).
 *
 * Sem eles, "(status A ou B ou C) E (serviço X ou Y)" é inexprimível: o filtro
 * tem um balde de "ou" só. Quem precisava disso repetia a condição no grupo
 * "e" — `status = A E status = B` —, que não casa nada, e o kanban ficava
 * vazio sem erro nenhum. Quatro kanbans de equipe estavam assim em produção.
 *
 * Em campo de texto livre eles não entram: escolher de uma lista é o que torna
 * a multi-seleção possível.
 */
const LIST_FILTER_OPERATORS = ['in', 'not_in'];

const LIST_OPERATOR_OPTIONS = [
  { value: 'in', label: 'É um de' },
  { value: 'not_in', label: 'Não é um de' },
];

const TEXT_OPERATOR_OPTIONS = [
  { value: 'equals', label: 'Igual a' },
  { value: 'not_equals', label: 'Diferente de' },
  { value: 'contains', label: 'Contém' },
  { value: 'not_contains', label: 'Não contém' },
  { value: 'starts_with', label: 'Começa com' },
  { value: 'ends_with', label: 'Termina com' },
  { value: 'is_null', label: 'É nulo' },
  { value: 'is_not_null', label: 'Não é nulo' },
];

const DATETIME_OPERATOR_OPTIONS = [
  // Períodos relativos primeiro — são os mais usados no dia a dia (item 52/9).
  ...DATE_PERIOD_OPERATORS.map((op) => ({ value: op, label: DATE_PERIOD_LABELS[op] })),
  { value: 'equals', label: 'Igual a (mesmo dia)' },
  { value: 'not_equals', label: 'Diferente do dia' },
  { value: 'greater_than', label: 'Posterior a' },
  { value: 'less_than', label: 'Anterior a' },
  { value: 'greater_than_or_equal', label: 'Em ou após' },
  { value: 'less_than_or_equal', label: 'Em ou antes de' },
  { value: 'contains', label: 'Contém (texto)' },
  { value: 'not_contains', label: 'Não contém (texto)' },
  { value: 'is_null', label: 'É nulo' },
  { value: 'is_not_null', label: 'Não é nulo' },
];

const AdvancedFilterModal = ({ 
  open, 
  handleClose, 
  fields = [], 
  onFilterApplied,
  isSlidePanel = false,
  onFilterSelectionChange,
  onSaveReady,
  initialFilter = null,
  initialMode = null,
  permissions = [],
  users = [],
  ...props
}) => {
  const { theme } = useContext(ThemeContext);
  const canCreateDefaultFilter = Array.isArray(permissions) && permissions.includes(PERMISSION_CREATE_DEFAULT_FILTERS);

  const [activeTab, setActiveTab] = useState(0);
  const [filters, setFilters] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  
  const [originalFilterState, setOriginalFilterState] = useState(null);
  
  const [filterName, setFilterName] = useState('');
  const [filterDescription, setFilterDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [isDefault, setIsDefault] = useState(false);
  const [visibleInPanel, setVisibleInPanel] = useState(true);

  const effectiveIsDefault = canCreateDefaultFilter ? isDefault : (selectedFilter?.is_default ?? false);

  useEffect(() => {
    if (isSlidePanel && onFilterSelectionChange) {
      onFilterSelectionChange(selectedFilter);
    }
  }, [selectedFilter, isSlidePanel, onFilterSelectionChange]);
  
  const [visibleFields, setVisibleFields] = useState({});
  
  const [conditions, setConditions] = useState([]);
  const [fieldConditions, setFieldConditions] = useState([]);
  // Lógica entre condições: 'AND' = corresponder a TODAS | 'OR' = a QUALQUER.
  const [conditionLogic, setConditionLogic] = useState('AND');

  const [viewType, setViewType] = useState('list');
  const [kanbanColumns, setKanbanColumns] = useState([]);
  const [ticketStatuses, setTicketStatuses] = useState([]);
  // `dragItem` guarda uma LISTA desde o K11: um arrasto comum é a lista de
  // tamanho 1, e o de vários leva a seleção inteira.
  const [dragItem, setDragItem] = useState(null);
  const [dragFromColumnId, setDragFromColumnId] = useState(null);

  // Quais justificativas estão marcadas, e de qual coluna. A seleção
  // pertence a UMA coluna -- ver `utils/selecaoDeJustificativas`.
  const [selecaoDeItens, setSelecaoDeItens] = useState(SELECAO_VAZIA);
  const [dragColumnIndex, setDragColumnIndex] = useState(null);

  const [conditionModalOpen, setConditionModalOpen] = useState(false);
  const [conditionModalType, setConditionModalType] = useState(null); 
  const [editingConditionIndex, setEditingConditionIndex] = useState(null);
  const [tempCondition, setTempCondition] = useState({
    field_name: '',
    field_id: '',
    operator: 'equals',
    value: '',
    field_type: 'string',
    logic_group: 'combined'
  });
  
  const [availableFields, setAvailableFields] = useState({
    ticket_fields: [],
    custom_fields: []
  });

  const loadTicketStatuses = useCallback(() => {
    return http.get('/ticket-statuses').then((res) => {
      const list = Array.isArray(res.data) ? res.data : [];
      setTicketStatuses(list);
      return list;
    }).catch(() => {
      setTicketStatuses([]);
      return [];
    });
  }, []);

  const kanbanConfigFilterRef = useRef(null);
  const kanbanReparsedRef = useRef(false);

  useEffect(() => {
    if (open) {
      loadFilters();
      loadAvailableFields();
      loadTicketStatuses();
      // Carrega os operadores para o autocomplete da condição "Responsável".
      if (!users || users.length === 0) {
        props.getUsers?.();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loadTicketStatuses]);

  useEffect(() => {
    if (!open || activeTab !== 3) return;
    loadTicketStatuses();
  }, [open, activeTab, loadTicketStatuses]);

  useEffect(() => {
    if (!open) return;
    if (viewType !== 'kanban') return;
    if (!ticketStatuses || ticketStatuses.length === 0) return;
    if (kanbanColumns && kanbanColumns.length > 0) return;
    setKanbanColumns(buildInitialKanbanColumnsFromStatuses(ticketStatuses));
  }, [open, viewType, ticketStatuses, kanbanColumns]);

  useEffect(() => {
    if (!open || viewType !== 'kanban' || !kanbanConfigFilterRef.current) return;
    if (kanbanReparsedRef.current) return;
    if (!ticketStatuses?.length) return;
    const rawKc = kanbanConfigFilterRef.current.settings?.kanban_columns;
    if (!Array.isArray(rawKc) || rawKc.length === 0) return;
    setKanbanColumns(parseKanbanColumnsFromFilter(rawKc, ticketStatuses));
    kanbanReparsedRef.current = true;
  }, [open, viewType, ticketStatuses]);

  // Garante a coluna de arquivo/lixeira ao editar um kanban: ela recebe os
  // status órfãos quando uma coluna é removida, evitando perda. Mantida sempre
  // por último; só é criada quando já existe ao menos uma coluna configurada.
  useEffect(() => {
    if (!open || viewType !== 'kanban') return;
    setKanbanColumns((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      if (prev.some(isArchiveColumn)) return prev;
      return [...prev, buildArchiveColumn()];
    });
  }, [open, viewType, kanbanColumns.length]);

  const kanbanAvailableItems = useMemo(
    () => getUnassignedKanbanItems(ticketStatuses, kanbanColumns),
    [ticketStatuses, kanbanColumns]
  );

  const loadFilters = async () => {
    try {
      const response = await http.get('/filters');
      setFilters(response.data);
    } catch (error) {
      console.error('Erro ao carregar filtros:', error);
    }
  };

  const loadAvailableFields = async () => {
    try {
      const response = await http.get('/filters/available/fields');
      let data = response.data || { ticket_fields: [], custom_fields: [] };
      const tf = Array.isArray(data.ticket_fields) ? data.ticket_fields : [];
      const catField = tf.find((f) => f.name === 'category');
      const hasCategoryOptions = Array.isArray(catField?.options) && catField.options.length > 0;
      if (!hasCategoryOptions) {
        try {
          const cr = await http.get('/ticket-categories');
          const list = Array.isArray(cr.data) ? cr.data : [];
          const opts = list.map((c) => ({ value: c.name, label: c.name }));
          if (opts.length > 0) {
            data = {
              ...data,
              ticket_fields: tf.map((f) =>
                f.name === 'category' ? { ...f, options: opts } : f
              ),
            };
          }
        } catch {
        }
      }

      let tf2 = Array.isArray(data.ticket_fields) ? data.ticket_fields : [];
      const sgField = tf2.find((f) => f.name === 'service_group_id');
      const hasSgOptions = Array.isArray(sgField?.options) && sgField.options.length > 0;
      if (!hasSgOptions) {
        try {
          const gr = await http.get('/groups');
          const glist = Array.isArray(gr.data) ? gr.data : [];
          const sgOpts = glist
            .filter((g) => g && (g.status === true || g.status === 1))
            .map((g) => ({
              value: Number(g.id),
              label: g.description || String(g.code ?? g.id),
            }))
            .sort((a, b) => String(a.label).localeCompare(String(b.label), 'pt'));
          if (sgOpts.length > 0) {
            if (sgField) {
              data = {
                ...data,
                ticket_fields: tf2.map((f) =>
                  f.name === 'service_group_id' ? { ...f, options: sgOpts } : f
                ),
              };
            } else {
              data = {
                ...data,
                ticket_fields: [
                  ...tf2,
                  {
                    name: 'service_group_id',
                    label: 'Grupo de atendimento',
                    type: 'integer',
                    options: sgOpts,
                  },
                ],
              };
            }
          }
        } catch {
        }
      }

      setAvailableFields(data);
    } catch (error) {
      console.error('Erro ao carregar campos disponíveis:', error);
    }
  };

  const getFieldOptions = (fieldName) => {
    if (!fieldName) return null;

    const dynamicTicketField = availableFields.ticket_fields?.find(f => f.name === fieldName);
    if (dynamicTicketField && Array.isArray(dynamicTicketField.options) && dynamicTicketField.options.length > 0) {
      return dynamicTicketField.options.map((opt) => ({
        value: opt.value ?? opt.id ?? opt.key,
        label: opt.label ?? opt.name ?? String(opt.value ?? opt.id ?? opt.key),
      }));
    }

    const dynamicCustomField = availableFields.custom_fields?.find(f => f.name === fieldName || f.id === fieldName);
    if (dynamicCustomField && Array.isArray(dynamicCustomField.options) && dynamicCustomField.options.length > 0) {
      return dynamicCustomField.options.map((opt) => ({
        value: opt.value ?? opt.id ?? opt.key,
        label: opt.label ?? opt.name ?? String(opt.value ?? opt.id ?? opt.key),
      }));
    }

    const fieldOptions = {
      status: [
        { value: 1, label: 'Aberto' },
        { value: 2, label: 'Pendente' },
        { value: 3, label: 'Resolvido' },
        { value: 4, label: 'Fechado' }
      ],
      priority: [
        { value: 0, label: 'Baixa' },
        { value: 1, label: 'Média' },
        { value: 2, label: 'Alta' },
        { value: 3, label: 'Urgente' }
      ],
      is_internal: [
        { value: 0, label: 'Público' },
        { value: 1, label: 'Interno' }
      ]
    };
    
    return fieldOptions[fieldName] || null;
  };

  const getValueLabel = (fieldName, value) => {
    if (value === null || value === undefined || value === '') {
      return '(sem valor)';
    }

    if (['created_at', 'resolved_at', 'updated_at'].includes(fieldName)) {
      const raw = Array.isArray(value) && value.length === 1 ? value[0] : value;
      const d = new Date(raw);
      if (!Number.isNaN(d.getTime())) return d.toLocaleString('pt-BR');
    }

    // Responsável / Criado por: mostra o NOME do operador em vez do id -- ou
    // "Agente logado", quando a condição usa o valor dinâmico.
    // Marcador dinâmico antes de qualquer busca em lista: ele não está nas
    // opções do campo (entra só no render do seletor), então a busca abaixo
    // devolveria o texto cru -- "__grupo_do_agente_logado__" na tela.
    if (ehValorDinamico(value)) {
      return ROTULOS[value];
    }

    if (aceitaAgenteLogado(fieldName)) {
      return rotuloDoOperador(value, users);
    }

    const options = getFieldOptions(fieldName);
    if (options) {
      // Condição de lista: cada valor pelo seu rótulo. Sem isto o card da
      // condição mostraria `[2,3,4]` -- o texto cru que está no banco.
      if (Array.isArray(value) || (typeof value === 'string' && value.trim().startsWith('['))) {
        const rotulos = valoresDaCondicao(value)
          .map((v) => options.find((opt) => String(opt.value) === String(v))?.label ?? v);

        return rotulos.length ? rotulos.join(', ') : '(sem valor)';
      }

      const numValue = typeof value === 'string' && !isNaN(value) ? parseInt(value) : value;
      const option = options.find(opt => opt.value === numValue || opt.value === value);
      return option ? option.label : value;
    }
    
    return value;
  };

  const renderValueField = (condition, index, isCustomField = false) => {
    const fieldName = isCustomField ? 
      availableFields.custom_fields?.find(f => f.id === condition.field_id)?.name :
      condition.field_name;
    
    const options = getFieldOptions(fieldName);
    
    if (options) {
      return (
        <FormControl fullWidth size="small">
          <InputLabel>Valor</InputLabel>
          <Select
            value={condition.value}
            onChange={(e) => updateCondition(index, 'value', e.target.value, isCustomField)}
            label="Valor"
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    
    const isTextField = ['title', 'description', 'client', 'assigned_to', 'created_by'].includes(fieldName);
    if (isTextField && !condition.operator) {
      updateCondition(index, 'operator', 'contains', isCustomField);
    }
    
    return (
      <TextField
        label="Valor"
        value={condition.value}
        onChange={(e) => updateCondition(index, 'value', e.target.value, isCustomField)}
        fullWidth
        size="small"
        placeholder={isTextField ? "Digite parte do texto..." : ""}
      />
    );
  };


  const handleSelectFilter = (filter) => {
    setSelectedFilter(filter);
    setFilterName(filter.name);
    setFilterDescription(filter.description || '');
    setIsPublic(filter.is_public || false);
    setIsDefault(filter.is_default || false);
    
    const fieldsMap = {};
    
    filter.fields?.forEach(field => {
      if (field.id) {
        fieldsMap[field.id] = field.pivot?.visible || false;
      }
    });
    
    if (filter.settings?.standard_fields) {
      filter.settings.standard_fields.forEach(field => {
        fieldsMap[field.name] = field.visible || false;
      });
    }
    
    setVisibleFields(fieldsMap);
    
    setConditions(filter.conditions || []);
    setFieldConditions(filter.field_conditions || []);

    const vt = filter.settings?.view_type || 'list';
    const rawKc = Array.isArray(filter.settings?.kanban_columns) ? filter.settings.kanban_columns : [];
    kanbanConfigFilterRef.current = vt === 'kanban' && rawKc.length > 0 ? filter : null;
    kanbanReparsedRef.current = Boolean(ticketStatuses?.length);
    const kc = parseKanbanColumnsFromFilter(rawKc, ticketStatuses);
    setViewType(vt);
    setKanbanColumns(kc);
    setVisibleInPanel(filter.settings?.visible_in_panel !== false);
    const cLogic = filter.settings?.condition_logic === 'OR' ? 'OR' : 'AND';
    setConditionLogic(cLogic);

    setOriginalFilterState({
      name: filter.name,
      description: filter.description || '',
      is_public: filter.is_public || false,
      is_default: filter.is_default || false,
      visibleFields: fieldsMap,
      conditions: filter.conditions || [],
      fieldConditions: filter.field_conditions || [],
      viewType: vt,
      kanbanColumns: kc,
      visibleInPanel: filter.settings?.visible_in_panel !== false,
      conditionLogic: cLogic
    });
    
    setIsCreating(false);
  };

  const handleCreateNew = () => {
    setSelectedFilter(null);
    setFilterName('');
    setFilterDescription('');
    setIsPublic(false);
    setIsDefault(false);
    setVisibleInPanel(true);
    setVisibleFields({});
    setConditions([]);
    setFieldConditions([]);
    setConditionLogic('AND');
    setViewType('list');
    setKanbanColumns([]);
    kanbanConfigFilterRef.current = null;
    kanbanReparsedRef.current = false;
    setOriginalFilterState(null);
    setIsCreating(true);
  };

  useEffect(() => {
    if (!open) return;
    if (initialMode === 'create') handleCreateNew();
    else if (initialMode === 'edit' && initialFilter) handleSelectFilter(initialFilter);
  }, [open, initialMode, initialFilter?.id]);

  const openConditionModal = (type = 'standard', index = null, group = 'combined') => {
    setActiveTab(2);
    setConditionModalType(type);
    setEditingConditionIndex(index);
    
    if (index !== null) {
      if (type === 'standard') {
        const existing = conditions[index] || {
          field_name: '',
          operator: 'equals',
          value: '',
          field_type: 'string'
        };
        const meta = availableFields.ticket_fields?.find((f) => f.name === existing.field_name);
        setTempCondition({
          ...existing,
          field_type: existing.field_type || meta?.type || 'string',
        });
      } else {
        setTempCondition(fieldConditions[index] || {
          field_id: '',
          operator: 'equals',
          value: '',
          field_type: 'string'
        });
      }
    } else {
      setTempCondition({
        field_name: '',
        field_id: '',
        operator: 'equals',
        value: '',
        field_type: 'string',
        logic_group: group
      });
    }

    setConditionModalOpen(true);
  };

  const closeConditionModal = () => {
    setConditionModalOpen(false);
    setEditingConditionIndex(null);
    setTempCondition({
      field_name: '',
      field_id: '',
      operator: 'equals',
      value: '',
      field_type: 'string'
    });
  };

  // Operadores que não exigem valor: is_null, is_not_null e os períodos
  // autocalculados (Hoje, Este mês...). O 'between' exige as duas datas.
  const operatorNeedsNoValue = (op) =>
    op === 'is_null' || op === 'is_not_null' || isValuelessDatePeriodOperator(op);

  const saveConditionFromModal = () => {
    if (conditionModalType === 'standard') {
      if (!tempCondition.field_name) {
        alert('Por favor, selecione o campo da condição.');
        return;
      }

      if (tempCondition.operator === 'between') {
        const arr = Array.isArray(tempCondition.value) ? tempCondition.value : [];
        if (!arr[0] || !arr[1]) {
          alert('Por favor, preencha as duas datas do intervalo.');
          return;
        }
      } else if (ehOperadorDeLista(tempCondition.operator)) {
        // Lista vazia salva vira filtro que não mostra nada -- exatamente o
        // sintoma que este operador veio consertar.
        if (valoresDaCondicao(tempCondition.value).length === 0) {
          alert('Por favor, escolha pelo menos um valor.');
          return;
        }
      } else if (!operatorNeedsNoValue(tempCondition.operator)) {
        if (tempCondition.value === null || tempCondition.value === undefined || tempCondition.value === '') {
          alert('Por favor, preencha o valor da condição.');
          return;
        }
      }

      let normalizedValue = tempCondition.value;
      const fieldOptions = getFieldOptions(tempCondition.field_name);

      // A lista já vem com o tipo certo de cada opção, e passar por baixo
      // custaria o array: `Number([5])` é 5 -- uma escolha só viraria escalar.
      if (ehOperadorDeLista(tempCondition.operator)) {
        normalizedValue = valoresDaCondicao(tempCondition.value);
      } else if (fieldOptions && fieldOptions.length > 0 && typeof fieldOptions[0].value === 'number') {
        const option = fieldOptions.find(opt => {
          const optValue = opt.value;
          const tempValue = tempCondition.value;
          return optValue === tempValue || 
                 optValue === Number(tempValue) ||
                 Number(optValue) === Number(tempValue) ||
                 String(optValue) === String(tempValue);
        });
        
        if (option) {
          normalizedValue = option.value;
        } else {
          if (tempCondition.value === 0 || tempCondition.value === '0') {
            normalizedValue = 0;
          } else {
            const numValue = Number(tempCondition.value);
            normalizedValue = isNaN(numValue) ? tempCondition.value : numValue;
          }
        }
      }
      
      const metaStd = availableFields.ticket_fields?.find((f) => f.name === tempCondition.field_name);
      const conditionToSave = {
        ...tempCondition,
        value: normalizedValue,
        field_type: metaStd?.type || tempCondition.field_type || 'string',
      };
      
      if (editingConditionIndex !== null) {
        const updated = [...conditions];
        updated[editingConditionIndex] = conditionToSave;
        setConditions(updated);
      } else {
        setConditions([...conditions, conditionToSave]);
      }
    } else {
      if (!tempCondition.field_id || (tempCondition.value === '' && tempCondition.value !== 0 && tempCondition.operator !== 'is_null' && tempCondition.operator !== 'is_not_null')) {
        alert('Por favor, preencha o campo e o valor da condição.');
        return;
      }
      
      if (tempCondition.operator !== 'is_null' && tempCondition.operator !== 'is_not_null') {
        if (tempCondition.value === null || tempCondition.value === undefined || tempCondition.value === '') {
          alert('Por favor, preencha o valor da condição.');
          return;
        }
      }
      
      const field = availableFields.custom_fields?.find(f => f.id === tempCondition.field_id);
      let normalizedValue = tempCondition.value;
      
      if (field && field.options && field.options.length > 0) {
        const firstOption = field.options[0];
        if (typeof firstOption.value === 'number' || (!isNaN(firstOption.value) && firstOption.value !== '')) {
          normalizedValue = tempCondition.value === '' ? '' : Number(tempCondition.value);
        }
      }
      
      const conditionToSave = {
        ...tempCondition,
        value: normalizedValue
      };
      
      if (editingConditionIndex !== null) {
        const updated = [...fieldConditions];
        updated[editingConditionIndex] = conditionToSave;
        setFieldConditions(updated);
      } else {
        setFieldConditions([...fieldConditions, conditionToSave]);
      }
    }
    
    closeConditionModal();
  };

  const addCondition = (group = 'combined') => {
    openConditionModal('standard', null, group);
  };

  const addFieldCondition = () => {
    openConditionModal('custom');
  };

  const updateCondition = (index, field, value, isCustomField = false) => {
    if (isCustomField) {
      const newFieldConditions = [...fieldConditions];
      newFieldConditions[index] = { ...newFieldConditions[index], [field]: value };
      setFieldConditions(newFieldConditions);
    } else {
      const newConditions = [...conditions];
      newConditions[index] = { ...newConditions[index], [field]: value };
      setConditions(newConditions);
    }
  };

  const updateFieldCondition = (index, field, value) => {
    const newFieldConditions = [...fieldConditions];
    newFieldConditions[index] = { ...newFieldConditions[index], [field]: value };
    setFieldConditions(newFieldConditions);
  };

  const removeCondition = (index) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const removeFieldCondition = (index) => {
    setFieldConditions(fieldConditions.filter((_, i) => i !== index));
  };

  /**
   * O recado da combinação que não casa nada, já com o nome que a pessoa vê.
   *
   * `null` quando não há o que dizer -- aviso à toa é aviso que se aprende a
   * ignorar. A regra (e o que de propósito NÃO é erro) mora no util.
   *
   * Declarado ANTES dos dois caminhos de salvar porque os dois o consultam:
   * ambos são `useCallback` com `conditions` nas dependências, então o valor
   * que eles enxergam acompanha a edição.
   */
  const avisoDeCombinacaoImpossivel = avisoDeCondicaoQueNuncaCasa(
    conditions,
    (campo) => availableFields.ticket_fields?.find((f) => f.name === campo)?.label || campo
  );

  /*
   * Pelo mesmo motivo do aviso acima, e lado a lado com ele: os dois tratam
   * do filtro que é salvo sem poder fazer o que a pessoa pediu. A diferença
   * é o resultado -- aquele não mostra NADA, este mostra TUDO.
   */
  const avisoDePendencia = avisoDeCondicaoNaoAdicionada({
    editorAberto: conditionModalOpen,
    tipo: conditionModalType,
    condicao: tempCondition,
  });

  const handleSave = useCallback(async () => {
    try {
      if (!filterName.trim()) {
        alert('Por favor, informe o nome do filtro.');
        return false;
      }
      if (viewType === 'kanban' && kanbanColumns.length > 0) {
        const hasEmptyColumn = kanbanColumns.some((c) => !isArchiveColumn(c) && (!Array.isArray(c.items) || c.items.length === 0));
        if (hasEmptyColumn) {
          toast.warning('Atenção! É necessário informar ao menos um item em cada coluna.');
          return false;
        }
      }

      // Salvar um filtro que não pode casar nada seria gravar o problema que
      // isto veio consertar -- e ele não dá erro depois, só silêncio.
      if (avisoDeCombinacaoImpossivel) {
        toast.warning(avisoDeCombinacaoImpossivel);
        setActiveTab(2);
        return false;
      }

      // A condição preenchida e não adicionada se perderia aqui embaixo, no
      // `conditions.filter(...)`, sem nada na tela. Ver `condicaoNaoAdicionada`.
      if (avisoDePendencia) {
        toast.warning(avisoDePendencia);
        setActiveTab(2);
        return false;
      }

      const standardFields = [];
      const customFields = [];

      Object.entries(visibleFields).forEach(([fieldKey, visible]) => {
        if (visible) {
          if (isNaN(parseInt(fieldKey))) {
            standardFields.push({ name: fieldKey, visible });
          } else {
            customFields.push({ id: parseInt(fieldKey), visible });
          }
        }
      });

      const filterData = {
        name: filterName.trim(),
        description: filterDescription.trim(),
        is_public: isPublic,
        is_default: effectiveIsDefault,
        fields: customFields,
        conditions: conditions.filter((c) => {
          if (!c.field_name) return false;
          if (operatorNeedsNoValue(c.operator)) return true;
          return c.value !== null && c.value !== undefined && c.value !== '';
        }),
        field_conditions: fieldConditions.filter((c) => {
          if (!c.field_id) return false;
          if (operatorNeedsNoValue(c.operator)) return true;
          return c.value !== null && c.value !== undefined && c.value !== '';
        }),
        standard_fields: standardFields,
        settings: {
          view_type: viewType,
          kanban_columns: kanbanColumns,
          visible_in_panel: visibleInPanel,
          condition_logic: conditionLogic
        }
      };

      if (selectedFilter) {
        await props.updateFilter({ id: selectedFilter.id, ...filterData });
      } else {
        const created = await props.addFilter(filterData);
        await loadFilters();
        if (created?.id) {
          handleSelectFilter(created);
          setIsCreating(false);
          kanbanConfigFilterRef.current = null;
          kanbanReparsedRef.current = false;
        }
        toast.success('Filtro criado com sucesso.');
        handleClose();
        return;
      }

      await loadFilters();
      
      if (selectedFilter) {
        const updatedFieldsMap = {};
        standardFields.forEach(field => {
          updatedFieldsMap[field.name] = field.visible;
        });
        customFields.forEach(field => {
          updatedFieldsMap[field.id] = true;
        });
        
        const newOriginalState = {
          name: filterName.trim(),
          description: filterDescription.trim(),
          is_public: isPublic,
          is_default: effectiveIsDefault,
          visibleFields: updatedFieldsMap,
          conditions: filterData.conditions,
          fieldConditions: filterData.field_conditions,
          viewType,
          kanbanColumns,
          visibleInPanel
        };
        
        setOriginalFilterState(newOriginalState);
        
        const updatedFilter = {
          ...selectedFilter,
          name: filterName.trim(),
          description: filterDescription.trim(),
          is_public: isPublic,
          is_default: effectiveIsDefault,
          conditions: filterData.conditions,
          field_conditions: filterData.field_conditions,
          settings: { ...selectedFilter.settings, view_type: viewType, kanban_columns: kanbanColumns, visible_in_panel: visibleInPanel, condition_logic: conditionLogic }
        };
        setSelectedFilter(updatedFilter);
      }

      toast.success('Filtro atualizado com sucesso.');
      if (!isSlidePanel) {
        handleClose();
      }
    } catch (error) {
      console.error('Erro ao salvar filtro:', error);
      const msg = error.response?.data?.message || error.message || 'Erro desconhecido';
      toast.error(`Erro ao salvar filtro: ${msg}`);
      throw error;
    }
    /*
     * `avisoDePendencia` PRECISA estar aqui.
     *
     * Ele não vem de `conditions` -- vem do editor aberto (`tempCondition`),
     * e nenhuma dessas três peças está na lista. Sem o valor na dependência,
     * o callback guarda a versão de quando `conditions` mudou pela última
     * vez: vazia, justamente no caso que a guarda existe para pegar. A
     * guarda compilaria, passaria na suíte e nunca dispararia na tela.
     */
  }, [filterName, filterDescription, isPublic, isDefault, visibleFields, conditions, fieldConditions, viewType, kanbanColumns, selectedFilter, isSlidePanel, handleClose, props, loadFilters, avisoDePendencia]);

  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  const hasFilterChanged = useCallback(() => {
    if (!selectedFilter || !originalFilterState) {
      return false;
    }

    if (filterName.trim() !== originalFilterState.name) {
      return true;
    }

    if (filterDescription.trim() !== originalFilterState.description) {
      return true;
    }

    if (isPublic !== originalFilterState.is_public || isDefault !== originalFilterState.is_default) {
      return true;
    }

    const currentVisibleFieldsStr = JSON.stringify(visibleFields);
    const originalVisibleFieldsStr = JSON.stringify(originalFilterState.visibleFields);
    if (currentVisibleFieldsStr !== originalVisibleFieldsStr) {
      return true;
    }

    const currentConditionsStr = JSON.stringify(conditions);
    const originalConditionsStr = JSON.stringify(originalFilterState.conditions);
    if (currentConditionsStr !== originalConditionsStr) {
      return true;
    }

    const currentFieldConditionsStr = JSON.stringify(fieldConditions);
    const originalFieldConditionsStr = JSON.stringify(originalFilterState.fieldConditions);
    if (currentFieldConditionsStr !== originalFieldConditionsStr) {
      return true;
    }

    if (viewType !== (originalFilterState.viewType || 'list')) {
      return true;
    }
    const serializeKanban = (cols) => JSON.stringify((cols || []).map((c) => ({
      id: typeof c === 'object' && c?.id != null ? c.id : c,
      name: c?.name,
      items: Array.isArray(c?.items) ? c.items : []
    })));
    if (serializeKanban(kanbanColumns) !== serializeKanban(originalFilterState.kanbanColumns)) {
      return true;
    }

    if (visibleInPanel !== (originalFilterState.visibleInPanel ?? true)) {
      return true;
    }

    if (conditionLogic !== (originalFilterState.conditionLogic || 'AND')) {
      return true;
    }

    return false;
  }, [selectedFilter, originalFilterState, filterName, filterDescription, isPublic, isDefault, visibleFields, conditions, fieldConditions, viewType, kanbanColumns, visibleInPanel, conditionLogic]);

  const canSave = useMemo(() => {
    if (isCreating) {
      return filterName.trim().length > 0;
    }
    if (selectedFilter && originalFilterState) {
      return hasFilterChanged() && filterName.trim().length > 0;
    }
    return false;
  }, [isCreating, selectedFilter, originalFilterState, filterName, hasFilterChanged]);

  useEffect(() => {
    if (isSlidePanel && onSaveReady) {
      const stableHandleSave = () => {
        return handleSaveRef.current();
      };
      
      const stableHandleSaveAndApply = () => {
        return handleSaveAndApplyRef.current();
      };
      
      onSaveReady({
        handleSave: stableHandleSave,
        handleSaveAndApply: stableHandleSaveAndApply,
        canSave,
        isCreating,
        hasChanges: selectedFilter ? hasFilterChanged() : false
      });
    }
  }, [isSlidePanel, onSaveReady, canSave, isCreating, selectedFilter, hasFilterChanged]); 

  const handleApply = async () => {
    if (selectedFilter) {
      onFilterApplied?.(selectedFilter);
      if (!isSlidePanel) {
        handleClose();
      }
    }
  };

  const handleSaveAndApply = useCallback(async () => {
    try {
      if (!filterName.trim()) {
        alert('Por favor, informe o nome do filtro.');
        return false;
      }
      if (viewType === 'kanban' && kanbanColumns.length > 0) {
        const hasEmptyColumn = kanbanColumns.some((c) => !isArchiveColumn(c) && (!Array.isArray(c.items) || c.items.length === 0));
        if (hasEmptyColumn) {
          toast.warning('Atenção! É necessário informar ao menos um item em cada coluna.');
          return false;
        }
      }

      // Salvar um filtro que não pode casar nada seria gravar o problema que
      // isto veio consertar -- e ele não dá erro depois, só silêncio.
      if (avisoDeCombinacaoImpossivel) {
        toast.warning(avisoDeCombinacaoImpossivel);
        setActiveTab(2);
        return false;
      }

      // A condição preenchida e não adicionada se perderia aqui embaixo, no
      // `conditions.filter(...)`, sem nada na tela. Ver `condicaoNaoAdicionada`.
      if (avisoDePendencia) {
        toast.warning(avisoDePendencia);
        setActiveTab(2);
        return false;
      }

      const standardFields = [];
      const customFields = [];

      Object.entries(visibleFields).forEach(([fieldKey, visible]) => {
        if (visible) {
          if (isNaN(parseInt(fieldKey))) {
            standardFields.push({ name: fieldKey, visible });
          } else {
            customFields.push({ id: parseInt(fieldKey), visible });
          }
        }
      });

      const filterData = {
        name: filterName.trim(),
        description: filterDescription.trim(),
        is_public: isPublic,
        is_default: effectiveIsDefault,
        fields: customFields,
        conditions: conditions.filter((c) => {
          if (!c.field_name) return false;
          if (operatorNeedsNoValue(c.operator)) return true;
          return c.value !== null && c.value !== undefined && c.value !== '';
        }),
        field_conditions: fieldConditions.filter((c) => {
          if (!c.field_id) return false;
          if (operatorNeedsNoValue(c.operator)) return true;
          return c.value !== null && c.value !== undefined && c.value !== '';
        }),
        standard_fields: standardFields,
        settings: {
          view_type: viewType,
          kanban_columns: kanbanColumns,
          visible_in_panel: visibleInPanel,
          condition_logic: conditionLogic
        }
      };

      if (selectedFilter) {
        await props.updateFilter({ id: selectedFilter.id, ...filterData });
        await loadFilters();
        const updatedFilter = {
          ...selectedFilter,
          name: filterName.trim(),
          description: filterDescription.trim(),
          is_public: isPublic,
          is_default: effectiveIsDefault,
          conditions: filterData.conditions,
          field_conditions: filterData.field_conditions,
          settings: {
            ...selectedFilter.settings,
            standard_fields: standardFields,
            view_type: viewType,
            kanban_columns: kanbanColumns,
            visible_in_panel: visibleInPanel
          }
        };
        setSelectedFilter(updatedFilter);
        onFilterApplied?.(updatedFilter);
        if (isSlidePanel) {
          handleClose();
        }
        return;
      }

      const created = await props.addFilter(filterData);
      await loadFilters();
      if (created?.id) {
        onFilterApplied?.(created);
      }
      toast.success('Filtro criado com sucesso.');
      if (isSlidePanel) {
        handleClose();
      }
    } catch (error) {
      console.error('Erro ao salvar e aplicar filtro:', error);
      const msg = error.response?.data?.message || error.message || 'Erro desconhecido';
      toast.error(`Erro ao salvar filtro: ${msg}`);
      throw error;
    }
    // `avisoDePendencia` pelo mesmo motivo do outro caminho de salvamento.
  }, [filterName, filterDescription, isPublic, isDefault, visibleFields, conditions, fieldConditions, viewType, kanbanColumns, selectedFilter, isSlidePanel, handleClose, onFilterApplied, props, loadFilters, avisoDePendencia]);

  const handleSaveAndApplyRef = useRef(handleSaveAndApply);
  handleSaveAndApplyRef.current = handleSaveAndApply;

  const handleDelete = async (filter) => {
    if (window.confirm(`Tem certeza que deseja deletar o filtro "${filter.name}"?`)) {
      try {
        await props.deleteFilter(filter);
        await loadFilters();
        if (selectedFilter?.id === filter.id) {
          setSelectedFilter(null);
        }
      } catch (error) {
        console.error('Erro ao deletar filtro:', error);
      }
    }
  };


  const conditionModalUsesDatetimeOperators = useMemo(() => {
    if (conditionModalType !== 'standard' || !tempCondition.field_name) return false;
    const meta = availableFields.ticket_fields?.find((f) => f.name === tempCondition.field_name);
    return meta?.type === 'datetime' || ['created_at', 'resolved_at', 'updated_at'].includes(tempCondition.field_name);
  }, [conditionModalType, tempCondition.field_name, availableFields.ticket_fields]);

  // O campo da condição em edição tem lista de opções? É isso que libera
  // "É um de" / "Não é um de" -- a multi-seleção precisa de algo para
  // escolher.
  const conditionModalFieldName = conditionModalType === 'standard'
    ? tempCondition.field_name
    : availableFields.custom_fields?.find((f) => f.id === tempCondition.field_id)?.name;
  const conditionModalHasOptions = Boolean(getFieldOptions(conditionModalFieldName));

  useEffect(() => {
    if (!conditionModalOpen) return;
    const allowed = [
      ...(conditionModalUsesDatetimeOperators ? DATETIME_FILTER_OPERATORS : TEXT_FILTER_OPERATORS),
      ...(conditionModalHasOptions ? LIST_FILTER_OPERATORS : []),
    ];
    setTempCondition((prev) => {
      if (prev.operator && !allowed.includes(prev.operator)) {
        return { ...prev, operator: 'equals' };
      }
      return prev;
    });
  }, [conditionModalOpen, conditionModalUsesDatetimeOperators, conditionModalHasOptions]);

  const renderModalValueField = () => {
    // Períodos relativos autocalculados (Hoje, Este mês...) não pedem valor.
    if (isValuelessDatePeriodOperator(tempCondition.operator)) {
      return (
        <TextField
          label="Valor"
          value={`${DATE_PERIOD_LABELS[tempCondition.operator]} (calculado automaticamente)`}
          fullWidth
          size="small"
          disabled
        />
      );
    }

    // Intervalo: duas datas (início e fim). Guardadas como array [inicio, fim].
    if (tempCondition.operator === 'between') {
      const arr = Array.isArray(tempCondition.value)
        ? tempCondition.value
        : String(tempCondition.value || '').split(',');
      const startVal = arr[0] ? new Date(arr[0]) : null;
      const endVal = arr[1] ? new Date(arr[1]) : null;
      const setRange = (idx, iso) => {
        const next = [arr[0] || '', arr[1] || ''];
        next[idx] = iso;
        setTempCondition({ ...tempCondition, value: next });
      };
      return (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <DatePicker
            label="De"
            value={startVal}
            onChange={(v) => setRange(0, v ? v.toISOString() : '')}
            ampm={false}
            slotProps={{ textField: { fullWidth: true, size: 'small', variant: 'outlined' } }}
          />
          <DatePicker
            label="Até"
            value={endVal}
            onChange={(v) => setRange(1, v ? v.toISOString() : '')}
            ampm={false}
            slotProps={{ textField: { fullWidth: true, size: 'small', variant: 'outlined' } }}
          />
        </Box>
      );
    }

    // Mês específico: seletor de mês (guardado como string "YYYY-MM").
    if (tempCondition.operator === 'specific_month') {
      return (
        <TextField
          label="Mês"
          type="month"
          value={typeof tempCondition.value === 'string' ? tempCondition.value : ''}
          onChange={(e) => setTempCondition({ ...tempCondition, value: e.target.value })}
          fullWidth
          size="small"
          InputLabelProps={{ shrink: true }}
        />
      );
    }

    const fieldNameToCheck = conditionModalType === 'standard'
      ? tempCondition.field_name
      : availableFields.custom_fields?.find(f => f.id === tempCondition.field_id)?.name;

    // Responsável / Criado por: autocomplete com os NOMES dos operadores (mesma
    // experiência da Busca Avançada) — digita pra filtrar e clica. Guarda o ID.
    if (aceitaAgenteLogado(fieldNameToCheck)) {
      // "Agente logado" entra como pseudo-operador no topo da lista: o
      // `<Autocomplete>` passa a tratá-lo como qualquer outro -- escolher,
      // exibir e devolver o valor -- em vez de exigir um caminho especial em
      // cada um desses três lugares. Quem resolve o valor é o servidor, na
      // hora de aplicar o filtro.
      const opcoes = opcoesDeOperador(users);
      const selectedUser = opcoes.find((u) => String(u.id) === String(tempCondition.value)) || null;
      return (
        <Autocomplete
          options={opcoes}
          getOptionLabel={(o) => o?.name || ''}
          value={selectedUser}
          onChange={(_, newValue) =>
            setTempCondition({ ...tempCondition, value: newValue ? newValue.id : '' })
          }
          isOptionEqualToValue={(o, v) => String(o.id) === String(v?.id)}
          noOptionsText="Nenhum operador"
          renderInput={(params) => (
            <TextField {...params} label="Valor" placeholder="Digite o nome do operador" size="small" fullWidth />
          )}
          renderOption={(propsOption, option) => (
            <li {...propsOption} key={option.id}>{option.name}</li>
          )}
        />
      );
    }

    const options = getFieldOptions(fieldNameToCheck);

    if (options) {
      // Os valores dinâmicos entram SÓ aqui, no render -- não em
      // `getFieldOptions`. O caminho de salvar (`normalizedValue`) também lê
      // aquela função e decide converter para número olhando o PRIMEIRO
      // item: um marcador de texto na frente desligaria a conversão do campo
      // inteiro, mudando o comportamento de quem nem usa valor dinâmico.
      const opcoes = [
        ...valoresDinamicosDe(fieldNameToCheck).map((valor) => ({
          value: valor,
          label: ROTULOS[valor],
        })),
        ...options,
      ];

      // "É um de" / "Não é um de": a MESMA lista, com múltipla escolha. O
      // valor vira array, e é como array que ele vai para o banco (JSON numa
      // coluna de texto) e volta -- ver `utils/condicaoDeLista`.
      if (ehOperadorDeLista(tempCondition.operator)) {
        const escolhidos = valoresDaCondicao(tempCondition.value).map((v) => String(v));
        // `options`, e não `opcoes`: os valores DINÂMICOS ("Agente logado",
        // "Não concluído") ficam de fora da múltipla escolha. Eles são
        // marcadores que o servidor resolve na hora, um por condição --
        // dentro de uma lista virariam texto cru comparado com a coluna.
        const opcoesDaLista = options;

        return (
          <FormControl fullWidth>
            <InputLabel>Valores</InputLabel>
            <Select
              multiple
              value={escolhidos}
              onChange={(e) => {
                const brutos = e.target.value;
                // De volta ao tipo da opção: o `value` do MUI vem string, e a
                // condição guarda número quando a opção é numérica.
                const valores = brutos.map((v) => {
                  const opcao = opcoesDaLista.find((o) => String(o.value) === String(v));
                  return opcao ? opcao.value : v;
                });
                setTempCondition({ ...tempCondition, value: valores });
              }}
              input={<OutlinedInput label="Valores" />}
              renderValue={(selecionados) => (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {selecionados.map((v) => (
                    <Chip
                      key={v}
                      size="small"
                      label={opcoesDaLista.find((o) => String(o.value) === String(v))?.label ?? v}
                    />
                  ))}
                </Box>
              )}
            >
              {opcoesDaLista.map((option) => (
                <MenuItem key={option.value} value={String(option.value)}>
                  <Checkbox checked={escolhidos.includes(String(option.value))} size="small" />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      }

      return (
        <FormControl fullWidth>
          <InputLabel>Valor</InputLabel>
          <Select
            value={tempCondition.value}
            onChange={(e) => {
              const selectedValue = e.target.value;
              // Marcador escolhido vai inteiro, sem passar pela conversão
              // numérica abaixo -- que o transformaria em `NaN`.
              if (ehValorDinamico(selectedValue)) {
                setTempCondition({ ...tempCondition, value: selectedValue });
                return;
              }
              const option = opcoes.find(opt => {
                const optValue = opt.value;
                return optValue === selectedValue || 
                       optValue === Number(selectedValue) ||
                       Number(optValue) === Number(selectedValue) ||
                       String(optValue) === String(selectedValue);
              });
              const finalValue = option ? option.value : selectedValue;
              setTempCondition({ ...tempCondition, value: finalValue });
            }}
            label="Valor"
          >
            {opcoes.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    const selectedStdMeta = conditionModalType === 'standard'
      ? availableFields.ticket_fields?.find((f) => f.name === fieldNameToCheck)
      : null;
    const isDatetimeField = selectedStdMeta?.type === 'datetime'
      || ['created_at', 'resolved_at', 'updated_at'].includes(fieldNameToCheck);

    if (isDatetimeField) {
      return (
        <DatePicker
          label="Valor (data e hora)"
          value={tempCondition.value ? new Date(tempCondition.value) : null}
          onChange={(newVal) => {
            setTempCondition({
              ...tempCondition,
              value: newVal ? newVal.toISOString() : '',
            });
          }}
          ampm={false}
          slotProps={{
            textField: { fullWidth: true, size: 'small', variant: 'outlined' },
          }}
        />
      );
    }
    
    return (
      <TextField
        label="Valor"
        value={tempCondition.value}
        onChange={(e) => setTempCondition({ ...tempCondition, value: e.target.value })}
        fullWidth
        placeholder="Digite o valor..."
      />
    );
  };

  const operatorOptions = [
    ...(conditionModalUsesDatetimeOperators ? DATETIME_OPERATOR_OPTIONS : TEXT_OPERATOR_OPTIONS),
    // Só com lista de opções: ver LIST_OPERATOR_OPTIONS.
    ...(conditionModalHasOptions ? LIST_OPERATOR_OPTIONS : []),
  ];

  const handleConditionFieldChange = (e) => {
    const value = e.target.value;
    if (!value) return;
    const [type, raw] = String(value).split(':');

    if (type === 'custom') {
      setConditionModalType('custom');
      setTempCondition((prev) => ({
        ...prev,
        field_id: raw,
        field_name: '',
        value: '',
      }));
    } else {
      setConditionModalType('standard');
      const tf = availableFields.ticket_fields?.find((f) => f.name === raw);
      const isDt = tf?.type === 'datetime';
      setTempCondition((prev) => {
        const nextOp = isDt && ['contains', 'not_contains', 'starts_with', 'ends_with'].includes(prev.operator)
          ? 'equals'
          : prev.operator;
        return {
          ...prev,
          field_name: raw,
          field_id: '',
          value: '',
          field_type: tf?.type || 'string',
          operator: nextOp,
        };
      });
    }
  };

  const conditionFieldSelectValue = conditionModalType === 'custom'
    ? (tempCondition.field_id ? `custom:${tempCondition.field_id}` : '')
    : (tempCondition.field_name ? `standard:${tempCondition.field_name}` : '');

  // Renderiza os cards de um conjunto de condições (já com o índice original,
  // para editar/remover). Usado nas duas seções: combinadas e independentes.
  const renderConditionItems = (items) => items.map(({ type, index, condition }, idx) => {
    const isStandard = type === 'standard';
    const field = isStandard
      ? availableFields.ticket_fields?.find(f => f.name === condition.field_name)
      : availableFields.custom_fields?.find(f => f.id === condition.field_id);

    const operatorLabels = {
      ...DATE_PERIOD_LABELS,
      equals: 'Igual a',
      not_equals: 'Diferente de',
      contains: 'Contém',
      not_contains: 'Não contém',
      starts_with: 'Começa com',
      ends_with: 'Termina com',
      greater_than: 'Posterior a',
      less_than: 'Anterior a',
      greater_than_or_equal: 'Em ou após',
      less_than_or_equal: 'Em ou antes de',
      is_null: 'É nulo',
      is_not_null: 'Não é nulo',
      in: 'É um de',
      not_in: 'Não é um de',
      not_between: 'Fora do intervalo',
    };

    const valueFieldName = isStandard ? condition.field_name : field?.name || condition.field_id;
    const valueLabel = getValueLabel(valueFieldName, condition.value);

    return (
      <Card key={`${type}-${index}-${idx}`} sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                {field?.label || field?.name || valueFieldName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {operatorLabels[condition.operator] || condition.operator}
                {operatorNeedsNoValue(condition.operator) ? '' : `: ${valueLabel}`}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                size="small"
                label={isStandard ? 'Campo padrão' : 'Campo customizável'}
                variant="outlined"
              />
              <IconButton
                onClick={() => openConditionModal(isStandard ? 'standard' : 'custom', index)}
                color="primary"
                size="small"
              >
                <EditIcon />
              </IconButton>
              <IconButton
                onClick={() => (isStandard ? removeCondition(index) : removeFieldCondition(index))}
                color="error"
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  });

  const renderConditionForm = () => {
    if (!conditionModalOpen) return null;

    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          bgcolor: 'action.hover',
          borderColor: 'divider',
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          {editingConditionIndex !== null ? 'Editar condição' : 'Nova condição'}
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              select
              label="Campo"
              fullWidth
              value={conditionFieldSelectValue}
              onChange={handleConditionFieldChange}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
            >
              <option value="" disabled>
                Selecione o campo
              </option>
              {availableFields.ticket_fields?.length > 0 && (
                <optgroup label="Campos padrão">
                  {/* display_only (ex.: Serviço Grupo/Subgrupo) são só para exibir
                      como coluna/card; não entram como condição de filtro. */}
                  {availableFields.ticket_fields.filter((field) => !field.display_only).map((field) => (
                    <option key={`standard-${field.name}`} value={`standard:${field.name}`}>
                      {field.label}
                    </option>
                  ))}
                </optgroup>
              )}
              {availableFields.custom_fields?.length > 0 && (
                <optgroup label="Campos customizáveis">
                  {availableFields.custom_fields.map((field) => (
                    <option key={`custom-${field.id}`} value={`custom:${field.id}`}>
                      {field.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </TextField>

            <FormControl component="fieldset" fullWidth>
              <Typography component="legend" variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                Operador
              </Typography>
              <RadioGroup
                value={tempCondition.operator || 'equals'}
                onChange={(e) => setTempCondition((prev) => ({ ...prev, operator: e.target.value }))}
              >
                <Grid container spacing={0}>
                  {operatorOptions.map((opt) => (
                    <Grid item xs={12} sm={6} key={opt.value}>
                      <FormControlLabel
                        value={opt.value}
                        control={<Radio size="small" />}
                        label={opt.label}
                        sx={{ m: 0, py: 0.25 }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </RadioGroup>
            </FormControl>

            {tempCondition.operator !== 'is_null' && tempCondition.operator !== 'is_not_null' && (
              renderModalValueField()
            )}

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <Button onClick={closeConditionModal} variant="outlined" size="small">
                Cancelar
              </Button>
              <Button
                onClick={saveConditionFromModal}
                variant="contained"
                color="primary"
                size="small"
                startIcon={<SaveIcon />}
              >
                {editingConditionIndex !== null ? 'Salvar alterações' : 'Adicionar condição'}
              </Button>
            </Box>
          </Box>
        </LocalizationProvider>
      </Paper>
    );
  };

  /**
   * Move um ou VÁRIOS itens para a coluna (K11).
   *
   * Passou a receber lista em vez de item único. O corpo é o de antes, com o
   * laço por fora: um arrasto de um item só é a lista de tamanho 1, e segue
   * fazendo exatamente o que fazia.
   *
   * Em UMA passada sobre o estado, e não um `setKanbanColumns` por item: com
   * dez justificativas seriam dez atualizações encadeadas, e as intermediárias
   * ficariam visíveis como a coluna se montando aos pedaços.
   */
  const moveKanbanItemsToColumn = (itens, sourceColumnId, targetColumnId) => {
    const lista = (Array.isArray(itens) ? itens : [itens]).filter((it) => kanbanItemKey(it));
    if (lista.length === 0 || sourceColumnId === targetColumnId) return;

    if (sourceColumnId === 'available') {
      setKanbanColumns((prev) =>
        prev.map((col) => {
          if (col.id !== targetColumnId) return col;
          const items = [...(col.items || [])];
          lista.forEach((it) => {
            const key = kanbanItemKey(it);
            if (!items.some((ja) => kanbanItemKey(ja) === key)) items.push(it);
          });
          return { ...col, items };
        })
      );
      return;
    }

    const next = kanbanColumns.map((col) => ({ ...col, items: [...(col.items || [])] }));
    const sourceIdx = next.findIndex((c) => c.id === sourceColumnId);
    const targetIdx = next.findIndex((c) => c.id === targetColumnId);
    if (sourceIdx === -1 || targetIdx === -1) return;

    lista.forEach((it) => {
      const key = kanbanItemKey(it);
      const srcItems = next[sourceIdx].items;
      // Item que não está na origem é ignorado, e não aborta o resto: num
      // arrasto de dez, um desencontro não pode cancelar os outros nove.
      if (!srcItems.some((ja) => kanbanItemKey(ja) === key)) return;
      next[sourceIdx].items = srcItems.filter((ja) => kanbanItemKey(ja) !== key);
      const tgtItems = next[targetIdx].items;
      if (!tgtItems.some((ja) => kanbanItemKey(ja) === key)) {
        next[targetIdx].items = [...tgtItems, it];
      }
    });

    setKanbanColumns(next);
  };

  const removeKanbanItemsFromColumn = (itens, sourceColumnId) => {
    const chaves = (Array.isArray(itens) ? itens : [itens])
      .map((it) => kanbanItemKey(it))
      .filter(Boolean);

    if (chaves.length === 0 || sourceColumnId === 'available') return;

    setKanbanColumns((prev) =>
      prev.map((col) => {
        if (col.id !== sourceColumnId) return col;
        return {
          ...col,
          items: (col.items || []).filter((it) => !chaves.includes(kanbanItemKey(it))),
        };
      })
    );
  };

  const renderKanbanDraggableItem = (it, itIdx, columnId, itensDaColuna = []) => {
    const selecionado = estaSelecionado(selecaoDeItens, columnId, it);
    const st = ticketStatuses.find((s) => s.id === it.status_id);
    const hasJustification = it.substatus_id != null && it.substatus_id !== '';
    const sub =
      hasJustification && st
        ? (Array.isArray(st.substatuses) ? st.substatuses.find((s) => s.id === it.substatus_id) : null)
        : null;
    const subLabel = sub?.name;
    return (
      <Box
        key={`${columnId}-${kanbanItemKey(it)}-${itIdx}`}
        draggable
        onClick={() => setSelecaoDeItens((atual) => alternarSelecao(atual, columnId, it))}
        onDragStart={() => {
          const normalizado = { status_id: it.status_id, substatus_id: it.substatus_id ?? null };
          // Arrastar um item marcado leva a seleção inteira; arrastar um de
          // fora dela leva só ele E descarta a seleção -- a pessoa mudou de
          // ideia, e um arrasto comum não pode carregar junto o que ela
          // esqueceu marcado.
          const levados = itensParaArrastar(selecaoDeItens, columnId, normalizado, itensDaColuna);
          setDragItem(levados);
          setDragFromColumnId(columnId);
          if (!estaSelecionado(selecaoDeItens, columnId, normalizado)) {
            setSelecaoDeItens(SELECAO_VAZIA);
          }
        }}
        sx={{
          cursor: 'grab',
          // A marca da seleção é a BORDA e um fundo tênue derivado da cor
          // primária, e não uma cor fixa: o painel tem tema claro e escuro, e
          // um cinza chapado some num dos dois.
          border: `1px solid ${selecionado ? theme.palette.primary.main : theme.palette.divider}`,
          boxShadow: selecionado ? `0 0 0 1px ${theme.palette.primary.main}` : 'none',
          borderRadius: 1.5,
          overflow: 'hidden',
          backgroundColor: selecionado ? theme.palette.action.selected : theme.palette.background.paper,
          '&:active': { cursor: 'grabbing' },
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            px: 1.25,
            py: subLabel ? 0.5 : 0.75,
            fontWeight: 700,
            fontSize: '0.8rem',
            color: theme.palette.text.primary,
            ...(subLabel ? { borderBottom: `1px solid ${theme.palette.divider}` } : {}),
          }}
        >
          {st?.name ?? `Status ${it.status_id}`}
        </Typography>
        {subLabel ? (
          <Typography variant="caption" sx={{ px: 1.25, py: 0.75, display: 'block', color: theme.palette.text.secondary }}>
            {subLabel}
          </Typography>
        ) : null}
      </Box>
    );
  };

  const modalContent = (
    <>
      {!isSlidePanel && (
        <DialogTitle sx={{ 
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <FilterListIcon />
          <Typography variant="h6" component="div">
            Configuração Avançada de Filtros
          </Typography>
        </DialogTitle>
      )}

      <Box sx={{ padding: isSlidePanel ? 0 : 0, height: isSlidePanel ? '100%' : 'auto', minHeight: 0, flex: isSlidePanel ? 1 : undefined }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          height: isSlidePanel ? '100%' : '78vh',
          minHeight: isSlidePanel ? 0 : 520,
        }}>
          {/* Painel esquerdo - Lista de filtros */}
          <Box sx={{ 
            width: isSlidePanel ? { xs: '100%', sm: 200 } : { xs: '100%', sm: 220 },
            maxWidth: isSlidePanel ? { sm: 200 } : { sm: 220 },
            borderRight: { xs: 'none', sm: `1px solid ${theme.palette.divider}` },
            borderBottom: { xs: `1px solid ${theme.palette.divider}`, sm: 'none' },
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
          }}>
            <Box sx={{ p: isSlidePanel ? 1.5 : 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateNew}
                fullWidth
                sx={{ mb: 1 }}
              >
                Novo Filtro
              </Button>
              <Typography variant="body2" color="text.secondary">
                {filters.length} filtro(s) disponível(is)
              </Typography>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto' }}>
              <List dense>
                {filters.map((filter) => (
                  <ListItem
                    key={filter.id}
                    button
                    selected={selectedFilter?.id === filter.id}
                    onClick={() => handleSelectFilter(filter)}
                    sx={{
                      '&.Mui-selected': {
                        backgroundColor: theme.palette.primary.light,
                        '&:hover': {
                          backgroundColor: theme.palette.primary.light,
                        }
                      }
                    }}
                  >
                    <ListItemText
                      primary={filter.name}
                      primaryTypographyProps={{
                        noWrap: true,
                        fontSize: '0.875rem',
                        fontWeight: selectedFilter?.id === filter.id ? 600 : 400,
                      }}
                      secondary={
                        ehCompartilhado(filter) ? (
                          <Box sx={{ display: 'flex', gap: 0.25, mt: 0.25, flexWrap: 'wrap' }}>
                            {rotulosDeCompartilhamento(filter).map((rotulo) => (
                              <Chip
                                key={rotulo}
                                label={rotulo}
                                size="small"
                                color={rotulo === ROTULO_EQUIPE ? 'primary' : 'default'}
                                variant={rotulo === ROTULO_EQUIPE ? 'filled' : 'outlined'}
                                sx={{ height: 18, fontSize: '0.65rem' }}
                              />
                            ))}
                          </Box>
                        ) : null
                      }
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Deletar filtro">
                        <IconButton
                          edge="end"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(filter);
                          }}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>

          {/* Painel direito - Configuração do filtro */}
          <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            minWidth: 0, 
            overflow: 'hidden'
          }}>
            {selectedFilter || isCreating ? (
              <>
                {/* Tabs de configuração */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs 
                    value={activeTab} 
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    sx={{
                      '& .MuiTab-root': {
                        color: theme.palette.text.secondary,
                        fontWeight: 500,
                        textTransform: 'none',
                        minHeight: 48,
                        '&.Mui-selected': {
                          color: theme.palette.text.primary,
                          // backgroundColor: theme.palette.background.default,
                          fontWeight: 600
                        }
                      },
                      '& .MuiTabs-indicator': {
                        backgroundColor: theme.palette.text.primary,
                        height: 3
                      }
                    }}
                  >
                    <Tab label="Informações" />
                    <Tab label="Campos Visíveis" />
                    <Tab label="Condições" />
                    <Tab label="Visualização" />
                  </Tabs>
                </Box>

                {/* Conteúdo das tabs */}
                <Box sx={{ 
                  flex: 1, 
                  overflow: 'auto', 
                  p: isSlidePanel ? { xs: 2, sm: 4 } : { xs: 1.5, sm: 2 },
                  minHeight: 0
                }}>
                  {activeTab === 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: isSlidePanel ? 4 : 2 }}>
                      <TextField
                        label="Nome do Filtro"
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        fullWidth
                        required
                      />
                      <TextField
                        label="Descrição"
                        value={filterDescription}
                        onChange={(e) => setFilterDescription(e.target.value)}
                        fullWidth
                        multiline
                        rows={3}
                      />
                      {/*
                        * Um caminho só para compartilhar (ponto 8 da Gisele).
                        *
                        * Havia dois interruptores que faziam a mesma coisa --
                        * deixar o filtro visível para todo mundo -- e só um
                        * pedia permissão. Os rótulos ("Público" e "Padrão")
                        * não diziam a ninguém qual era qual, e pelo "público"
                        * qualquer operador publicava filtro para a empresa
                        * inteira. Decisão dele: o "público" sai, e quem
                        * compartilha precisa da permissão.
                        */}
                      {canCreateDefaultFilter && (
                        <Box>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={isDefault}
                                onChange={(e) => setIsDefault(e.target.checked)}
                              />
                            }
                            label="Visível para toda a equipe"
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1 }}>
                            Todos os operadores passam a ver este filtro, e ele aparece no início da lista.
                          </Typography>
                        </Box>
                      )}
                      {/*
                        * O interruptor do caminho ANTIGO, só para desligar.
                        *
                        * Existem filtros compartilhados pelo "público" de
                        * antes. Sem isto, o dono não teria como PARAR de
                        * compartilhar -- e parar não precisa de permissão
                        * nenhuma, é desfazer.
                        */}
                      {mostrarOInterruptorAntigo(isPublic) && (
                        <Box>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={isPublic}
                                onChange={(e) => setIsPublic(e.target.checked)}
                              />
                            }
                            label="Visível para toda a equipe (compartilhamento antigo)"
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1 }}>
                            Este filtro foi compartilhado pelo caminho antigo. Ao desligar, ele volta a ser só seu.
                          </Typography>
                        </Box>
                      )}
                      <FormControlLabel
                        control={
                          <Switch
                            checked={visibleInPanel}
                            onChange={(e) => setVisibleInPanel(e.target.checked)}
                          />
                        }
                        label="Habilitar o uso da visualização (exibe na listagem de painéis)"
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1 }}>
                        Desabilite se não quiser que este filtro apareça como opção ao selecionar filtro na página de Tickets.
                      </Typography>
                    </Box>
                  )}

                  {activeTab === 1 && (
                    <Box>
                      <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                        Campos Padrão do Ticket
                      </Typography>
                      <Grid container spacing={1}>
                        {availableFields.ticket_fields?.map((field) => (
                          <Grid item xs={12} sm={6} md={4} lg={3} key={field.name}>
                            <Chip
                              label={field.label}
                              variant={visibleFields[field.name] ? "filled" : "outlined"}
                              color={visibleFields[field.name] ? "primary" : "default"}
                              deleteIcon={visibleFields[field.name] ? <CloseIcon /> : <AddIcon />}
                              onDelete={() => setVisibleFields({
                                ...visibleFields,
                                [field.name]: !visibleFields[field.name]
                              })}
                              onClick={() => setVisibleFields({
                                ...visibleFields,
                                [field.name]: !visibleFields[field.name]
                              })}
                              sx={{
                                width: '100%',
                                height: 'auto',
                                borderRadius: 2,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease-in-out',
                                whiteSpace: 'normal',
                                lineHeight: 1.2,
                                alignItems: 'flex-start',
                                py: 1,
                                '& .MuiChip-label': {
                                  whiteSpace: 'normal',
                                  overflow: 'visible',
                                  textOverflow: 'unset',
                                  display: 'block'
                                },
                                '&:hover': {
                                  transform: 'translateY(-1px)',
                                  boxShadow: 2
                                }
                              }}
                            />
                          </Grid>
                        ))}
                      </Grid>

                      <Divider sx={{ my: 3 }} />

                      <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                        Campos Customizáveis
                      </Typography>
                      <Grid container spacing={1}>
                        {availableFields.custom_fields?.map((field) => (
                          <Grid item xs={12} sm={6} md={4} lg={3} key={field.id}>
                            <Chip
                              label={field.name}
                              variant={visibleFields[field.id] ? "filled" : "outlined"}
                              color={visibleFields[field.id] ? "primary" : "default"}
                              deleteIcon={visibleFields[field.id] ? <CloseIcon /> : <AddIcon />}
                              onDelete={() => setVisibleFields({
                                ...visibleFields,
                                [field.id]: !visibleFields[field.id]
                              })}
                              onClick={() => setVisibleFields({
                                ...visibleFields,
                                [field.id]: !visibleFields[field.id]
                              })}
                              sx={{
                                width: '100%',
                                height: 'auto',
                                borderRadius: 2,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease-in-out',
                                whiteSpace: 'normal',
                                lineHeight: 1.2,
                                alignItems: 'flex-start',
                                py: 1,
                                '& .MuiChip-label': {
                                  whiteSpace: 'normal',
                                  overflow: 'visible',
                                  textOverflow: 'unset',
                                  display: 'block'
                                },
                                '&:hover': {
                                  transform: 'translateY(-1px)',
                                  boxShadow: 2
                                }
                              }}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {activeTab === 2 && (
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Condições de Filtro
                      </Typography>

                      {/*
                        * O aviso da combinação impossível.
                        *
                        * Repetir o mesmo campo com "Igual a" nas combinadas é
                        * pedir `status = Aberto E status = Pendente`: nenhum
                        * chamado atende, e a tela diz "Nenhum ticket
                        * encontrado" igual a quando de fato não há nada. Foi
                        * esse silêncio que escondeu quatro kanbans de equipe
                        * em produção. Ver `utils/condicaoQueNuncaCasa`.
                        */}
                      {avisoDeCombinacaoImpossivel && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          {avisoDeCombinacaoImpossivel}
                        </Alert>
                      )}

                      {renderConditionForm()}

                      {(() => {
                        const allItems = [
                          ...conditions.map((condition, index) => ({ type: 'standard', index, condition })),
                          ...fieldConditions.map((condition, index) => ({ type: 'custom', index, condition })),
                        ];
                        const isIndep = (it) => (it.condition.logic_group || 'combined') === 'independent';
                        const combined = allItems.filter((it) => !isIndep(it));
                        const independent = allItems.filter(isIndep);
                        return (
                          <>
                            {/* Condições combinadas (E) */}
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1 }}>
                              Condições combinadas
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              O ticket deverá corresponder a todas as condições:
                            </Typography>
                            {combined.length === 0 && !conditionModalOpen && (
                              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                                Nenhuma condição combinada.
                              </Typography>
                            )}
                            {renderConditionItems(combined)}
                            {!conditionModalOpen && (
                              <Button
                                startIcon={<AddIcon />}
                                onClick={() => addCondition('combined')}
                                variant="outlined"
                                fullWidth
                                sx={{ mt: 1, mb: 3 }}
                              >
                                Adicionar condição combinada
                              </Button>
                            )}

                            {/* Condições independentes (OU) */}
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1 }}>
                              Condições independentes
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              O ticket deverá corresponder a ao menos uma das condições:
                            </Typography>
                            {independent.length === 0 && !conditionModalOpen && (
                              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                                Nenhuma condição independente.
                              </Typography>
                            )}
                            {renderConditionItems(independent)}
                            {!conditionModalOpen && (
                              <Button
                                startIcon={<AddIcon />}
                                onClick={() => addCondition('independent')}
                                variant="outlined"
                                fullWidth
                                sx={{ mt: 1 }}
                              >
                                Adicionar condição independente
                              </Button>
                            )}

                            {combined.length > 0 && independent.length > 0 && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                                Ao utilizar ambos os tipos de condições, os tickets precisarão atender a
                                todas as condições combinadas e também a pelo menos uma das condições independentes.
                              </Typography>
                            )}
                          </>
                        );
                      })()}
                    </Box>
                  )}
                  {activeTab === 3 && (
                    <Box>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        Tipo de visualização
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Escolha como os tickets serão exibidos ao aplicar este filtro. Kanban permite definir colunas que agrupam um ou mais status.
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                        <Chip
                          icon={<ViewListIcon />}
                          label="Listagem"
                          onClick={() => setViewType('list')}
                          color={viewType === 'list' ? 'primary' : 'default'}
                          variant={viewType === 'list' ? 'filled' : 'outlined'}
                          sx={{ cursor: 'pointer' }}
                        />
                        <Chip
                          icon={<ViewModuleIcon />}
                          label="Kanban"
                          onClick={() => setViewType('kanban')}
                          color={viewType === 'kanban' ? 'primary' : 'default'}
                          variant={viewType === 'kanban' ? 'filled' : 'outlined'}
                          sx={{ cursor: 'pointer' }}
                        />
                      </Box>

                      {viewType === 'kanban' && (
                        <>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
                            Colunas do Kanban
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Cada coluna representa uma etapa do fluxo (por exemplo: Novo, Em atendimento, Aguardando). Dentro de cada coluna você escolhe quais status pertencem a ela.
                          </Typography>

                          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={<AddIcon />}
                              onClick={() => {
                                const newId = Date.now();
                                // No INÍCIO, e com "Arquivados" ainda no fim:
                                // a regra mora em `adicionarColunaNoInicio`.
                                setKanbanColumns((prev) =>
                                  adicionarColunaNoInicio(prev, { id: newId, name: 'Nova coluna', items: [] })
                                );
                              }}
                            >
                              Adicionar coluna
                            </Button>
                          </Box>

                          {/*
                            As colunas ficam numa FILA HORIZONTAL que rola, e nao
                            quebram para a linha de baixo.

                            Gisele: "conforme acrescentar permanecer na horizontal,
                            como algumas colunas ja tem muita justificativa fica
                            muito extenso". Com `wrap`, cada coluna nova descia para
                            uma segunda linha -- e como a altura da tela e ditada
                            pela coluna MAIS CHEIA, duas linhas de coluna cheia
                            faziam a tela de configuracao crescer sem limite.

                            `nowrap` sozinho nao bastaria: sem `overflowX` as
                            colunas seriam cortadas. Elas param de encolher no
                            `minWidth: 220` de cada uma, e dai o container rola.
                          */}
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'nowrap',
                              overflowX: 'auto',
                              gap: 2,
                              pb: 2,
                              minHeight: 320,
                              alignItems: 'stretch'
                            }}
                          >
                            {kanbanColumns.map((col, index) => {
                              const colItems = Array.isArray(col.items) ? col.items : [];
                              const isDraggingColumn = dragColumnIndex === index;
                              const archived = isArchiveColumn(col);
                              return (
                                <Box
                                  key={`${col.id}-${index}`}
                                  onDragOver={(e) => {
                                    e.preventDefault();
                                    if (dragColumnIndex == null) return;
                                    e.dataTransfer.dropEffect = 'move';
                                  }}
                                  onDrop={(e) => {
                                    e.preventDefault();
                                    if (dragColumnIndex == null) return;
                                    // A coluna de arquivo fica sempre por último; não recebe reordenação.
                                    if (dragColumnIndex === index || archived) {
                                      setDragColumnIndex(null);
                                      return;
                                    }
                                    const next = [...kanbanColumns];
                                    const [removed] = next.splice(dragColumnIndex, 1);
                                    next.splice(index, 0, removed);
                                    setKanbanColumns(next);
                                    setDragColumnIndex(null);
                                  }}
                                  sx={{
                                    minWidth: 220,
                                    flex: '1 1 220px',
                                    maxWidth: 320,
                                    borderRadius: 2,
                                    border: archived
                                      ? `2px dashed ${theme.palette.divider}`
                                      : `2px solid ${isDraggingColumn ? theme.palette.primary.main : theme.palette.divider}`,
                                    backgroundColor: archived ? theme.palette.action.hover : theme.palette.background.paper,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    overflow: 'hidden',
                                    opacity: isDraggingColumn ? 0.85 : 1
                                  }}
                                >
                                  {archived ? (
                                    <Box
                                      sx={{
                                        p: 1.5,
                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        flexShrink: 0,
                                      }}
                                    >
                                      <ArchiveIcon sx={{ color: theme.palette.text.secondary, flexShrink: 0 }} fontSize="small" />
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
                                        {col.name || 'Arquivados'}
                                      </Typography>
                                    </Box>
                                  ) : (
                                  <Box
                                    draggable
                                    onDragStart={(e) => {
                                      setDragColumnIndex(index);
                                      e.dataTransfer.effectAllowed = 'move';
                                      e.dataTransfer.setData('text/plain', `column-${index}`);
                                    }}
                                    onDragEnd={() => setDragColumnIndex(null)}
                                    sx={{
                                      p: 1.5,
                                      borderBottom: `1px solid ${theme.palette.divider}`,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      gap: 0.5,
                                      flexShrink: 0,
                                      cursor: 'grab',
                                      '&:active': { cursor: 'grabbing' }
                                    }}
                                  >
                                    <DragIndicatorIcon sx={{ color: theme.palette.text.secondary, flexShrink: 0 }} fontSize="small" />
                                    <TextField
                                      value={col.name}
                                      onChange={(e) => {
                                        const next = [...kanbanColumns];
                                        next[index] = { ...next[index], name: e.target.value };
                                        setKanbanColumns(next);
                                      }}
                                      size="small"
                                      variant="standard"
                                      inputProps={{ style: { fontWeight: 600, fontSize: '0.95rem' } }}
                                      sx={{ flex: 1, minWidth: 0, '& .MuiInput-root': { fontSize: '0.95rem' } }}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const removed = kanbanColumns[index];
                                        const remaining = kanbanColumns.filter((_, i) => i !== index);
                                        const orphans = Array.isArray(removed.items) ? removed.items : [];
                                        if (orphans.length > 0) {
                                          // Move os status órfãos para a coluna de arquivo (lixeira)
                                          // em vez de misturá-los na primeira coluna do fluxo.
                                          const key = (it) => `${it.status_id}-${it.substatus_id ?? 'n'}`;
                                          let archiveIdx = remaining.findIndex(isArchiveColumn);
                                          if (archiveIdx === -1) {
                                            remaining.push(buildArchiveColumn());
                                            archiveIdx = remaining.length - 1;
                                          }
                                          const archive = { ...remaining[archiveIdx] };
                                          const seen = new Set((archive.items || []).map(key));
                                          const merged = [...(archive.items || [])];
                                          orphans.forEach((it) => {
                                            if (!seen.has(key(it))) { seen.add(key(it)); merged.push(it); }
                                          });
                                          remaining[archiveIdx] = { ...archive, items: merged };
                                        }
                                        setKanbanColumns(remaining);
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                  )}
                                  <Box
                                    sx={{
                                      flex: 1,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: 1.5,
                                      alignContent: 'flex-start',
                                      p: 1.5,
                                      minHeight: 200,
                                      borderRadius: 0,
                                      border: 'none',
                                      backgroundColor: theme.palette.action.hover
                                    }}
                                    onDragOver={(e) => {
                                      e.preventDefault();
                                    }}
                                    onDrop={(e) => {
                                      e.preventDefault();
                                      if (!dragItem || dragFromColumnId == null) return;
                                      moveKanbanItemsToColumn(dragItem, dragFromColumnId, col.id);
                                      setDragItem(null);
                                      setDragFromColumnId(null);
                                      // A seleção morre no destino: ela
                                      // apontava para itens que já saíram da
                                      // coluna de origem, e ficaria marcando
                                      // o que não existe mais ali.
                                      setSelecaoDeItens(SELECAO_VAZIA);
                                    }}
                                  >
                                    {colItems.map((it, itIdx) => renderKanbanDraggableItem(it, itIdx, col.id, colItems))}
                                    {archived && colItems.length === 0 && (
                                      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
                                        Status removidos de outras colunas ficam guardados aqui sem serem perdidos. Arraste-os de volta para reutilizá-los.
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>

                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
                            Status e justificativas disponíveis
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            Status e justificativas que ainda não estão em nenhuma coluna (por exemplo, criados depois do filtro, ou que ficaram sem coluna). Arraste-os para a coluna desejada para reutilizá-los — nada é descartado.
                          </Typography>
                          {/*
                            Sem esta linha ninguém descobre que dá para
                            clicar: o item sempre foi só arrastável, e nada na
                            aparência dele sugere seleção. Uma funcionalidade
                            que a pessoa não encontra é a mesma coisa que não
                            existir.
                          */}
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            Para mover vários de uma vez, clique neles para marcar e arraste qualquer um dos marcados.
                            {quantidadeSelecionada(selecaoDeItens, selecaoDeItens.colunaId) > 0 && (
                              <strong>
                                {' '}
                                {quantidadeSelecionada(selecaoDeItens, selecaoDeItens.colunaId)} marcado(s).
                              </strong>
                            )}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 1.5,
                              p: 1.5,
                              minHeight: 72,
                              borderRadius: 2,
                              border: `2px dashed ${theme.palette.divider}`,
                              backgroundColor: theme.palette.action.hover,
                            }}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                              e.preventDefault();
                              if (!dragItem || dragFromColumnId == null) return;
                              removeKanbanItemsFromColumn(dragItem, dragFromColumnId);
                              setDragItem(null);
                              setDragFromColumnId(null);
                              setSelecaoDeItens(SELECAO_VAZIA);
                            }}
                          >
                            {kanbanAvailableItems.length === 0 ? (
                              <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
                                Todas as justificativas cadastradas já estão em alguma coluna.
                              </Typography>
                            ) : (
                              kanbanAvailableItems.map((it, itIdx) =>
                                renderKanbanDraggableItem(it, itIdx, 'available', kanbanAvailableItems)
                              )
                            )}
                          </Box>

                          {kanbanColumns.length === 0 && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ py: 2, textAlign: 'center' }}
                            >
                              Nenhuma coluna configurada. Clique em &quot;Adicionar coluna&quot; para começar.
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                  )}
                </Box>
              </>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                flexDirection: 'column',
                gap: isSlidePanel ? 4 : 2
              }}>
                <FilterListIcon sx={{ fontSize: isSlidePanel ? 80 : 64, color: theme.palette.text.secondary }} />
                <Typography variant="h6" color="text.secondary">
                  Selecione um filtro ou crie um novo
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {!isSlidePanel && (
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleClose} variant="outlined">
            Cancelar
          </Button>
          {(isCreating || (selectedFilter && canSave)) && (
            <Button onClick={handleSave} variant="contained" startIcon={<SaveIcon />}>
              Salvar
            </Button>
          )}
        </DialogActions>
      )}
    </>
  );

  if (isSlidePanel) {
    return modalContent;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: theme.shadows[8],
          width: 'min(1280px, 96vw)',
          maxWidth: '96vw',
          height: 'min(900px, 92vh)',
          maxHeight: '92vh',
        },
      }}
    >
      {modalContent}
    </Dialog>
  );
};

const mapStateToProps = state => ({
  permissions: state.user?.permissions || [],
  users: state.user?.users || []
});

const mapDispatchToProps = dispatch => ({
  addFilter: (filter) => dispatch(addFilter(filter)),
  updateFilter: (filter) => dispatch(updateFilter(filter)),
  deleteFilter: (filter) => dispatch(deleteFilter(filter)),
  fetchFilters: () => dispatch(fetchFilters()),
  getUsers: () => dispatch(getUsers())
});

export default connect(mapStateToProps, mapDispatchToProps)(AdvancedFilterModal);
